import { useMemo, useRef } from "react";

/** Liste qui garde ses éléments retirés le temps de leur sortie en fondu (règle d'Alexis : rien ne disparaît d'un
 *  coup). Chaque entrée dit si l'élément est encore dans la liste (`present`) ; un élément retiré reste à sa place,
 *  `present: false`, jusqu'au changement suivant de la liste — son `Presence` joue la sortie puis ne rend plus
 *  rien. Un élément remis (annulation) redevient présent et rejoue son entrée. */
export interface LeavingEntry<T> {
  key: string;
  item: T;
  present: boolean;
}

/** Fusion pure : les éléments de `next` dans leur ordre, plus ceux de `prev` qui viennent d'en sortir, chacun
 *  derrière son voisin précédent ; les éléments déjà sortis au tour précédent sont oubliés. */
export function mergeLeaving<T>(prev: readonly LeavingEntry<T>[], next: readonly T[], keyOf: (item: T) => string): LeavingEntry<T>[] {
  const out: LeavingEntry<T>[] = next.map((item) => ({ key: keyOf(item), item, present: true }));
  const present = new Set(out.map((e) => e.key));
  let anchor: string | null = null; // dernier élément de `prev` encore présent, ou retiré déjà placé
  for (const entry of prev) {
    if (present.has(entry.key)) {
      anchor = entry.key;
      continue;
    }
    if (!entry.present) continue;
    const at = anchor === null ? 0 : out.findIndex((e) => e.key === anchor) + 1;
    out.splice(at, 0, { key: entry.key, item: entry.item, present: false });
    anchor = entry.key;
  }
  return out;
}

/** `mergeLeaving` au fil des rendus : recalculée quand l'ensemble des clés change ; les éléments présents sont
 *  toujours ceux du rendu courant (renommage, couleur). */
export function useLeavingList<T>(items: readonly T[], keyOf: (item: T) => string): LeavingEntry<T>[] {
  const prev = useRef<LeavingEntry<T>[]>([]);
  const signature = items.map(keyOf).join("\u0000");
  // eslint-disable-next-line react-hooks/exhaustive-deps -- recalcul sur la seule liste des clés
  const merged = useMemo(() => mergeLeaving(prev.current, items, keyOf), [signature]);
  prev.current = merged;
  const current = new Map(items.map((item) => [keyOf(item), item]));
  return merged.map((entry) => (entry.present ? { ...entry, item: current.get(entry.key) ?? entry.item } : entry));
}
